import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import TopicGameRenderer from '../components/topic/TopicGameRenderer';
import TopNavBar from '../components/common/TopNavBar';
import { playWord } from '../components/topic/_audio';

// ─── HuPath v2 Design Tokens ──────────────────────────────────────────────
const HT = {
  yellow: '#FFD166', yellowSoft: '#FFE9A8', yellowBg: '#FFF8E7', yellowShadow: '#E0B554',
  green: '#88C057', greenDark: '#5E9E3E', greenSoft: '#D4E8C0', greenBg: '#E8F3D9',
  cream: '#FFF8E7',
  coral: '#FF8C42', coralDark: '#E76F51', coralPress: '#B8431E',
  pinkBg: '#FCE4E8', pink: '#F4A6B8',
  brown: '#8B6F47', brownDark: '#6B4F2B',
  text: '#2D2D2D', textSoft: '#6B6B6B', textMuted: '#9A9A9A',
  border: '#E8E0CC', white: '#FFFFFF', teal: '#5AC0B5',
};
const FONT_DISPLAY = '"Fredoka", "Baloo 2", "Nunito", sans-serif';
const FONT_BODY = '"Nunito", "Baloo 2", sans-serif';
const SHADOW_SM = '0 2px 6px rgba(139,111,71,0.08)';
const SHADOW_MD = '0 4px 16px rgba(139,111,71,0.10)';

const apiBase = () => process.env.REACT_APP_API_URL || '';
const authHeader = () => {
  const t = localStorage.getItem('accessToken');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

// Session targets
const TARGET_QUESTIONS = 30;
const INITIAL_BATCH = 15;
const EXTEND_BATCH = 15;
const EXTEND_TRIGGER_QUEUE_SIZE = 2;
const WRONG_STREAK_TO_SKIP = 2;
// Level (continuous) mode: flush mastery silently every N answers, never auto-end.
const FLUSH_EVERY = 10;
// Immersive themed games are full-bleed (viewport-sized) and were built for a
// slim-header page. In the ladder they need a slim header + no bordered card,
// otherwise they overflow below the fold and misalign with the header.
const IMMERSIVE_UI_KINDS = new Set(['gacha', 'monster_tap', 'train_spell', 'train_cloze', 'defender']);
// Correct answers auto-advance (fewer clicks for kids); wrong answers wait for
// a click so the child can study the right answer. Gacha is pure discovery —
// advance almost immediately after the card is closed.
const AUTO_ADVANCE_MS = 1400;
const AUTO_ADVANCE_GACHA_MS = 500;

// Inject keyframes (once)
let _animsInjected = false;
const injectAnims = () => {
  if (_animsInjected || typeof document === 'undefined' || document.getElementById('hupath-session-anims')) return;
  _animsInjected = true;
  const s = document.createElement('style');
  s.id = 'hupath-session-anims';
  s.textContent = `
    @keyframes hupathPop {
      0% { opacity: 0; transform: scale(0.4); }
      60% { transform: scale(1.18); }
      100% { opacity: 1; transform: scale(1); }
    }
    @keyframes hupathShake {
      0%, 100% { transform: translateX(0); }
      20% { transform: translateX(-12px); }
      40% { transform: translateX(12px); }
      60% { transform: translateX(-8px); }
      80% { transform: translateX(8px); }
    }
    @keyframes hupathBounce {
      0%, 100% { transform: translateY(0); }
      50% { transform: translateY(-12px); }
    }
    @keyframes hupathConfetti {
      0%   { transform: translateY(-40px) rotate(0deg); opacity: 1; }
      100% { transform: translateY(110vh) rotate(720deg); opacity: 0; }
    }
    @keyframes hupathFadeOut {
      0%, 70% { opacity: 1; }
      100% { opacity: 0; }
    }
    @keyframes hupathStarSpin {
      0% { transform: scale(0) rotate(-180deg); opacity: 0; }
      60% { transform: scale(1.3) rotate(20deg); opacity: 1; }
      100% { transform: scale(1) rotate(0deg); opacity: 1; }
    }
    .hu-pop { animation: hupathPop 0.45s cubic-bezier(.34,1.56,.64,1) both; }
    .hu-shake { animation: hupathShake 0.5s ease both; }
    .hu-bounce { animation: hupathBounce 1s ease infinite; }
    .hu-fade { animation: hupathFadeOut 1.2s ease forwards; }
    .hu-star-spin { animation: hupathStarSpin 0.6s cubic-bezier(.34,1.56,.64,1) both; }
  `;
  document.head.appendChild(s);
};

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// ─── Confetti (10-15 pieces, falling) ──────────────────────────────────────
const CONFETTI_COLORS = ['#FFD166', '#FF8C42', '#88C057', '#F4A6B8', '#5AC0B5', '#FFD23F'];
const Confetti = () => {
  const pieces = useMemo(() => Array.from({ length: 24 }).map((_, i) => ({
    id: i,
    left: Math.random() * 100,
    delay: Math.random() * 0.3,
    duration: 1.4 + Math.random() * 0.8,
    size: 8 + Math.random() * 10,
    color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
    rotate: Math.random() * 360,
    shape: i % 3, // 0:square 1:circle 2:triangle
  })), []);
  return (
    <div style={{
      position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 1500,
      overflow: 'hidden',
    }}>
      {pieces.map(p => (
        <div
          key={p.id}
          style={{
            position: 'absolute',
            top: -20, left: `${p.left}%`,
            width: p.size, height: p.size,
            background: p.shape === 2 ? 'transparent' : p.color,
            borderRadius: p.shape === 1 ? '50%' : 4,
            borderBottom: p.shape === 2 ? `${p.size}px solid ${p.color}` : 'none',
            borderLeft: p.shape === 2 ? `${p.size/2}px solid transparent` : 'none',
            borderRight: p.shape === 2 ? `${p.size/2}px solid transparent` : 'none',
            animation: `hupathConfetti ${p.duration}s linear ${p.delay}s forwards`,
            transform: `rotate(${p.rotate}deg)`,
          }}
        />
      ))}
    </div>
  );
};

// ─── Feedback toast — fixed at top of viewport so it never shifts layout or hides content ─
const FeedbackBanner = ({ correct }) => (
  <>
    {correct && <Confetti />}
    <div
      className="hu-pop"
      style={{
        position: 'fixed',
        top: 76,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 1500,
        display: 'flex', alignItems: 'center', gap: 14,
        padding: '10px 22px',
        background: correct
          ? `linear-gradient(135deg, ${HT.green}, ${HT.greenDark})`
          : `linear-gradient(135deg, ${HT.coral}, ${HT.coralDark})`,
        color: HT.white,
        borderRadius: 999,
        border: `3px solid ${HT.white}`,
        boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
        maxWidth: 'calc(100% - 32px)',
        pointerEvents: 'none',
      }}
    >
      <span style={{ fontSize: 32, lineHeight: 1 }} className={correct ? 'hu-star-spin' : ''}>
        {correct ? '🎉' : '💪'}
      </span>
      <div style={{ textAlign: 'left' }}>
        <div style={{
          fontFamily: FONT_DISPLAY, fontSize: 18, fontWeight: 700, lineHeight: 1.15,
        }}>
          {correct ? 'Tuyệt vời!' : 'Cố thêm nhé!'}
        </div>
        <div style={{ fontSize: 12, fontWeight: 600, opacity: 0.92, marginTop: 1 }}>
          {correct ? 'Bạn trả lời chính xác 🌟' : 'Xem đáp án đúng rồi qua câu kế nha'}
        </div>
      </div>
    </div>
  </>
);

// ─── Tier stars (5-step kid-friendly indicator) ──────────────────────────
const TierStars = ({ currentTier }) => (
  <div style={{
    display: 'inline-flex', alignItems: 'center', gap: 4,
    background: HT.white, border: `1px solid ${HT.border}`,
    padding: '4px 10px', borderRadius: 999, boxShadow: SHADOW_SM,
  }}>
    <span style={{ fontSize: 11, fontWeight: 700, color: HT.textMuted, fontFamily: FONT_DISPLAY, letterSpacing: 1 }}>
      CẤP
    </span>
    {[1, 2, 3, 4, 5].map(t => {
      const passed = t < currentTier;
      const active = t === currentTier;
      return (
        <span
          key={t}
          style={{
            fontSize: 18, lineHeight: 1,
            opacity: passed || active ? 1 : 0.25,
            filter: active ? 'drop-shadow(0 0 4px rgba(255,209,102,0.8))' : 'none',
            transition: 'opacity 0.2s, filter 0.2s',
          }}
        >{passed || active ? '⭐' : '☆'}</span>
      );
    })}
  </div>
);

// First tier >= `from` that actually has variants (ladders are sparse: words
// without images skip image tiers, and a returning word's saved current_tier
// may land on a gap). Falls back to any playable tier; null = nothing playable.
function firstPlayableTier(leafItem, from) {
  const gbt = leafItem.games_by_tier || {};
  for (let t = Math.max(1, from || 1); t <= 5; t++) {
    if ((gbt[String(t)] || []).length > 0) return t;
  }
  for (let t = 1; t <= 5; t++) {
    if ((gbt[String(t)] || []).length > 0) return t;
  }
  return null;
}

export default function TopicSessionPage({ mode = 'topic' } = {}) {
  const params = useParams();
  const isLevel = mode === 'level';
  const { tagId } = params;
  const levelName = params.levelName;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Word-book mode: ?tags=<id,id,...> restricts the level stream to chosen
  // words; the server reports exhausted when they're done and the session
  // auto-ends via the existing exhausted flow.
  const restrictTags = isLevel ? (searchParams.get('tags') || '') : '';

  // Where the fetch/back go depends on mode. `sessionKey` identifies this
  // session for the fetch effects (topic id or level name).
  const sessionKey = isLevel ? `${levelName}|${restrictTags}` : tagId;
  const backUrl = isLevel
    ? (restrictTags ? `/vocab/words?level=${encodeURIComponent(levelName)}` : '/dashboard')
    : `/vocab/words?topic=${tagId}`;
  const buildSessionUrl = (n, exclude) => {
    if (isLevel) {
      const ex = exclude ? `&exclude=${encodeURIComponent(exclude)}` : '';
      const tg = restrictTags ? `&tags=${encodeURIComponent(restrictTags)}` : '';
      return `${apiBase()}/client/vocab/level-session?level=${encodeURIComponent(levelName)}&n=${n}${ex}${tg}`;
    }
    const ex = exclude ? `&exclude=${encodeURIComponent(exclude)}` : '';
    return `${apiBase()}/client/topics/parents/${tagId}/session?n=${n}${ex}`;
  };

  useEffect(() => { injectAnims(); }, []);

  const [plan, setPlan] = useState([]);
  const [currentRound, setCurrentRound] = useState([]);
  const [pendingNextRound, setPendingNextRound] = useState([]);
  const [roundNumber, setRoundNumber] = useState(1);
  const [variantsUsed, setVariantsUsed] = useState({});
  const [parentName, setParentName] = useState('');
  const [loadedLeafCodes, setLoadedLeafCodes] = useState(new Set());
  const [exhausted, setExhausted] = useState(false);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [extending, setExtending] = useState(false);

  const [answers, setAnswers] = useState([]);
  const [questionKey, setQuestionKey] = useState(0);
  const [feedback, setFeedback] = useState(null); // { correct: bool } | null

  const startTime = useRef(Date.now());
  const sessionStart = useRef(Date.now());
  const [, setElapsedTick] = useState(0);
  const submittedRef = useRef(false);
  // Level mode: index up to which answers have already been POSTed (mastery persisted).
  const flushedCount = useRef(0);
  const flushing = useRef(false);
  // Level mode: accumulated results across silent flushes → final summary screen.
  const sessionTotals = useRef({ correct: 0, total: 0, deltas: [], stars: 0, newly_mastered: 0 });
  // Kid-UX: word-completed toast, mastered toast, re-teach recap, milestone break.
  const [doneWord, setDoneWord] = useState(null);          // word name that just finished its ladder
  const [masteredWords, setMasteredWords] = useState(null); // names reported newly_mastered by a flush
  const [recapWord, setRecapWord] = useState(null);        // {word, name_vi, image_url, definition_vi, tts}
  const [milestone, setMilestone] = useState(null);        // distinct-word count hitting a multiple of 10
  const milestonesShown = useRef(new Set());
  // Daily goal (level mode): baseline words practiced today (before this session).
  const [dailyGoal, setDailyGoal] = useState(null);        // {today, goal}
  const sessionWordsAtStart = useRef(new Set());

  const answeredCount = answers.length;

  useEffect(() => {
    const id = setInterval(() => setElapsedTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  // ── Initial fetch
  useEffect(() => {
    fetch(buildSessionUrl(INITIAL_BATCH), { headers: authHeader() })
      .then(r => r.json().then(d => ({ ok: r.ok, d })))
      .then(({ ok, d }) => {
        if (!ok) throw new Error(d.detail || 'Failed');
        const sp = d.session_plan || [];
        if (sp.length === 0) throw new Error(isLevel
          ? 'Cấp độ này chưa có từ vựng để học.'
          : 'Chủ điểm này chưa được sinh game.');
        setPlan(sp);
        setLoadedLeafCodes(new Set(sp.map(p => p.leaf.tag_code)));
        setExhausted(!!d.exhausted);
        setParentName(isLevel ? (d.level || levelName) : d.parent_topic_name);
        const initial = sp.map((p, i) => ({
          leafIdx: i,
          currentTier: firstPlayableTier(p, p.leaf.starting_tier),
          wrongCount: 0,
        })).filter(x => x.currentTier != null);
        setCurrentRound(shuffle(initial));
        setPendingNextRound([]);
        startTime.current = Date.now();
        sessionStart.current = Date.now();
      })
      .catch(e => setError(String(e.message || e)))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionKey]);

  // ── Daily goal baseline (level mode): words practiced today before this session
  useEffect(() => {
    if (!isLevel) return;
    fetch(`${apiBase()}/client/vocab/level-progress`, { headers: authHeader() })
      .then(r => (r.ok ? r.json() : null))
      .then(d => {
        if (d) setDailyGoal({ today: d.today_words || 0, goal: d.daily_goal || 10 });
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLevel]);

  // ── Lazy extend
  const totalActiveLeaves = currentRound.length + pendingNextRound.length;
  useEffect(() => {
    if (loading || extending || exhausted) return;
    // Topic mode caps at TARGET_QUESTIONS; level mode streams continuously.
    if (!isLevel && answeredCount >= TARGET_QUESTIONS) return;
    if (totalActiveLeaves > EXTEND_TRIGGER_QUEUE_SIZE) return;

    setExtending(true);
    const exclude = Array.from(loadedLeafCodes).join(',');
    fetch(
      buildSessionUrl(EXTEND_BATCH, exclude),
      { headers: authHeader() },
    )
      .then(r => r.json().then(d => ({ ok: r.ok, d })))
      .then(({ ok, d }) => {
        if (!ok) { console.warn('Extend failed:', d?.detail); return; }
        const newLeaves = d.session_plan || [];
        if (newLeaves.length > 0) {
          setPlan(curr => {
            const startIdx = curr.length;
            const merged = [...curr, ...newLeaves];
            setPendingNextRound(p => [
              ...p,
              ...newLeaves.map((leafItem, i) => ({
                leafIdx: startIdx + i,
                currentTier: firstPlayableTier(leafItem, leafItem.leaf.starting_tier),
                wrongCount: 0,
              })).filter(x => x.currentTier != null),
            ]);
            return merged;
          });
          setLoadedLeafCodes(curr => {
            const next = new Set(curr);
            for (const p of newLeaves) next.add(p.leaf.tag_code);
            return next;
          });
        }
        if (d.exhausted) setExhausted(true);
      })
      .catch(e => console.warn('Extend error:', e))
      .finally(() => setExtending(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalActiveLeaves, answeredCount, loading, extending, exhausted, loadedLeafCodes, sessionKey]);

  // ── Round transition
  useEffect(() => {
    if (currentRound.length === 0 && pendingNextRound.length > 0) {
      setCurrentRound(shuffle(pendingNextRound));
      setPendingNextRound([]);
      setRoundNumber(n => n + 1);
    }
  }, [currentRound.length, pendingNextRound]);

  const currentQuestion = useMemo(() => {
    if (currentRound.length === 0 || plan.length === 0) return null;
    const head = currentRound[0];
    const leaf = plan[head.leafIdx];
    if (!leaf) return null;
    const tierKey = String(head.currentTier);
    const variants = leaf.games_by_tier?.[tierKey] || [];
    if (variants.length === 0) return null;
    const usedKey = `${head.leafIdx}_${head.currentTier}`;
    const used = variantsUsed[usedKey] || new Set();
    let pickIdx = variants.findIndex((_, i) => !used.has(i));
    if (pickIdx < 0) pickIdx = 0;
    const pick = variants[pickIdx];
    return {
      ...pick,
      leafName: leaf.leaf.name,
      leafTagId: leaf.leaf.tag_id,
      leafCode: leaf.leaf.tag_code,
      tier: head.currentTier,
      startingTier: leaf.leaf.starting_tier,
      pickIdx,
      leafIdx: head.leafIdx,
      wrongCount: head.wrongCount,
    };
  }, [currentRound, plan, variantsUsed]);

  // POST a batch of answers → records attempt + updates mastery. `parent_topic_id`
  // is null in level mode (the attempt keys off each answer's leaf_tag_id + tier).
  const postAnswers = (batch) =>
    fetch(`${apiBase()}/client/topics/games/attempts`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeader() },
      body: JSON.stringify({
        parent_topic_id: isLevel ? null : tagId,
        answers: batch,
        duration_ms: Date.now() - sessionStart.current,
      }),
    }).then(r => r.json().then(d => ({ ok: r.ok, d })));

  const submitAll = (allAnswers) => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    setSubmitting(true);
    // Only the un-flushed tail goes to the server; the result screen is built
    // from the accumulated totals (earlier batches were already persisted).
    const batch = allAnswers.slice(flushedCount.current);
    const finish = () => {
      flushedCount.current = allAnswers.length;
      const t = sessionTotals.current;
      sessionStorage.setItem('topic_session_result', JSON.stringify({
        score: t.total ? Math.round((1000 * t.correct) / t.total) / 10 : 0,
        correct: t.correct,
        total: t.total,
        deltas: t.deltas,
        stars_earned: t.stars,
        parent_topic_id: tagId,
        parent_topic_name: parentName,
        questions: plan.map(p => ({ tag_id: p.leaf.tag_id, tag_name: p.leaf.name })),
      }));
      navigate(`/topics/${tagId}/result`);
    };
    if (batch.length === 0) { finish(); return; }
    postAnswers(batch)
      .then(({ ok, d }) => { if (ok) absorbResult(d, batch.length); })
      .catch(e => console.warn('Final submit error:', e))
      .finally(finish);
  };

  // Accumulate a flush/submit response into the session totals (level mode)
  // and surface a "mastered!" toast when the backend reports newly mastered words.
  const absorbResult = (d, batchLen) => {
    if (!d) return;
    const t = sessionTotals.current;
    t.correct += d.correct || 0;
    t.total += d.total || batchLen;
    t.stars += d.stars_earned || 0;
    t.newly_mastered += d.newly_mastered || 0;
    if (Array.isArray(d.deltas)) t.deltas.push(...d.deltas);
    if (d.newly_mastered > 0 && Array.isArray(d.deltas)) {
      const names = d.deltas
        .filter(x => x.newly_mastered)
        .map(x => (plan.find(p => p.leaf.tag_id === x.tag_id)?.leaf.name) || '')
        .filter(Boolean);
      if (names.length) setMasteredWords(names);
    }
  };

  // Silently persist mastery every FLUSH_EVERY answers (BOTH modes) — quitting
  // mid-session must never lose learning progress.
  useEffect(() => {
    const unflushed = answers.length - flushedCount.current;
    if (unflushed < FLUSH_EVERY || flushing.current || submittedRef.current) return;
    flushing.current = true;
    const upTo = answers.length;
    const batch = answers.slice(flushedCount.current, upTo);
    postAnswers(batch)
      .then(({ ok, d }) => { flushedCount.current = upTo; if (ok) absorbResult(d, batch.length); })
      .catch(e => console.warn('Flush error:', e))
      .finally(() => { flushing.current = false; });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answers]);

  // Keep answers in a ref so the unmount flush sees the latest list.
  const answersRef = useRef([]);
  useEffect(() => { answersRef.current = answers; }, [answers]);

  // Flush the un-persisted tail when the player leaves: SPA unmount AND hard
  // navigation / tab close (pagehide — React cleanup does not run on unload).
  const flushTail = () => {
    if (submittedRef.current) return;
    const batch = answersRef.current.slice(flushedCount.current);
    if (!batch.length) return;
    flushedCount.current = answersRef.current.length;
    fetch(`${apiBase()}/client/topics/games/attempts`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeader() },
      body: JSON.stringify({
        parent_topic_id: isLevel ? null : tagId,
        answers: batch,
        duration_ms: Date.now() - sessionStart.current,
      }),
      keepalive: true,
    }).catch(() => {});
  };
  const flushTailRef = useRef(flushTail);
  flushTailRef.current = flushTail;
  useEffect(() => {
    const onHide = () => flushTailRef.current();
    window.addEventListener('pagehide', onHide);
    return () => {
      window.removeEventListener('pagehide', onHide);
      flushTailRef.current();
    };
  }, []);

  // Auto-hide the mastered toast.
  useEffect(() => {
    if (!masteredWords) return;
    const t = setTimeout(() => setMasteredWords(null), 2600);
    return () => clearTimeout(t);
  }, [masteredWords]);

  // Recap card: speak the word on show, auto-dismiss after 5s.
  useEffect(() => {
    if (!recapWord) return;
    const cleanup = playWord(recapWord.audio_url, recapWord.tts);
    const t = setTimeout(() => setRecapWord(null), 5000);
    return () => { clearTimeout(t); if (cleanup) cleanup(); };
  }, [recapWord]);

  // Level mode: end session — flush remaining answers, then show the summary.
  const endLevelSession = () => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    setSubmitting(true);
    const batch = answers.slice(flushedCount.current);
    const done = () => {
      flushedCount.current = answers.length;
      const t = sessionTotals.current;
      if (t.total === 0) { navigate('/dashboard'); return; }
      const words = new Set(answers.map(a => a.leaf_tag_id)).size;
      sessionStorage.setItem('topic_session_result', JSON.stringify({
        level_mode: true,
        score: Math.round((100 * t.correct) / t.total * 10) / 10,
        correct: t.correct,
        total: t.total,
        deltas: t.deltas,
        stars_earned: t.stars,
        words_practiced: words,
        parent_topic_name: `Cấp độ ${parentName || levelName}`,
        questions: plan.map(p => ({ tag_id: p.leaf.tag_id, tag_name: p.leaf.name })),
      }));
      navigate(`/vocab/${encodeURIComponent(levelName)}/result`);
    };
    if (batch.length === 0) { done(); return; }
    postAnswers(batch)
      .then(({ ok, d }) => { if (ok) absorbResult(d, batch.length); })
      .catch(e => console.warn('Final flush error:', e))
      .finally(done);
  };

  // Snapshot of the question being answered, used by advanceToNext to update queues.
  const answeredSnapshotRef = useRef(null);

  const onAnswer = (ans) => {
    if (!currentQuestion || feedback) return; // ignore answers while feedback already showing
    const time_ms = Date.now() - startTime.current;
    const isCorrect = !!ans.is_correct;
    const newAnswer = {
      game_id: currentQuestion.game_id,
      leaf_tag_id: currentQuestion.leafTagId,
      tier: currentQuestion.tier,
      is_correct: isCorrect,
      time_ms,
    };
    const updatedAnswers = [...answers, newAnswer];
    setAnswers(updatedAnswers);
    setFeedback({ correct: isCorrect });
    answeredSnapshotRef.current = {
      leafIdx: currentQuestion.leafIdx,
      tier: currentQuestion.tier,
      wrongCount: currentQuestion.wrongCount,
      isCorrect,
      updatedAnswers,
    };

    const usedKey = `${currentQuestion.leafIdx}_${currentQuestion.tier}`;
    setVariantsUsed(prev => {
      const next = { ...prev };
      const set = new Set(next[usedKey] || []);
      set.add(currentQuestion.pickIdx);
      next[usedKey] = set;
      return next;
    });

    // Word completed its ladder? (correct + no playable tier above this one)
    if (isCorrect) {
      const gbt = plan[currentQuestion.leafIdx]?.games_by_tier || {};
      let hasHigher = false;
      for (let t = currentQuestion.tier + 1; t <= 5; t++) {
        if ((gbt[String(t)] || []).length > 0) { hasHigher = true; break; }
      }
      if (!hasHigher) setDoneWord(currentQuestion.leafName);
    }
  };

  // Build the re-teach recap for a word the child failed twice.
  const buildRecap = (leafIdx) => {
    const p = plan[leafIdx];
    if (!p) return null;
    const gacha = (p.games_by_tier?.['1'] || []).find(g => g.payload?.ui_kind === 'gacha');
    const pl = gacha?.payload || {};
    return {
      word: p.leaf.name,
      name_vi: p.leaf.name_vi || pl.name_vi || '',
      image_url: pl.image_url || null,
      definition_vi: pl.definition_vi || null,
      tts: pl.tts_text || p.leaf.name,
      audio_url: pl.audio_url || null,
    };
  };

  // User clicks "Câu tiếp →" — advance to the next question.
  const advanceToNext = () => {
    const snap = answeredSnapshotRef.current;
    if (!feedback || !snap) return;
    setFeedback(null);
    answeredSnapshotRef.current = null;

    // Topic mode ends at TARGET_QUESTIONS; level mode never auto-ends.
    if (!isLevel && snap.updatedAnswers.length >= TARGET_QUESTIONS) {
      submitAll(snap.updatedAnswers);
      return;
    }

    setCurrentRound(r => r.slice(1));
    setDoneWord(null);
    let nextEntry = null;
    if (snap.isCorrect) {
      // Advance to the NEXT TIER THAT HAS VARIANTS (words without images skip
      // image-based tiers, so the ladder can be sparse, e.g. 1→3→4→5).
      const gbt = plan[snap.leafIdx]?.games_by_tier || {};
      for (let t = snap.tier + 1; t <= 5; t++) {
        if ((gbt[String(t)] || []).length > 0) {
          nextEntry = { leafIdx: snap.leafIdx, currentTier: t, wrongCount: 0 };
          break;
        }
      }
    } else {
      const newWrong = snap.wrongCount + 1;
      if (newWrong < WRONG_STREAK_TO_SKIP) {
        nextEntry = { leafIdx: snap.leafIdx, currentTier: snap.tier, wrongCount: newWrong };
      } else {
        // Word skipped after 2 wrongs — re-teach it briefly before moving on.
        const recap = buildRecap(snap.leafIdx);
        if (recap) setRecapWord(recap);
      }
    }
    if (nextEntry) setPendingNextRound(p => [...p, nextEntry]);

    // Milestone break every 10 distinct words (level mode only).
    if (isLevel) {
      const distinct = new Set(snap.updatedAnswers.map(a => a.leaf_tag_id)).size;
      if (distinct > 0 && distinct % 10 === 0 && !milestonesShown.current.has(distinct)) {
        milestonesShown.current.add(distinct);
        setMilestone(distinct);
      }
    }

    startTime.current = Date.now();
    setQuestionKey(k => k + 1);
  };

  const handleEarlyEnd = () => {
    if (answers.length === 0) {
      navigate(backUrl);
      return;
    }
    if (isLevel) {
      // Continuous flow — end immediately (progress already saved incrementally).
      endLevelSession();
      return;
    }
    if (window.confirm(`Kết thúc sớm với ${answers.length} câu đã trả lời?`)) {
      submitAll(answers);
    }
  };

  // Auto-submit when buffers empty + exhausted
  useEffect(() => {
    if (loading || submitting || submittedRef.current) return;
    if (
      currentRound.length === 0 &&
      pendingNextRound.length === 0 &&
      exhausted &&
      !extending &&
      answers.length > 0
    ) {
      if (isLevel) endLevelSession();
      else submitAll(answers);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentRound.length, pendingNextRound.length, exhausted, extending, loading, submitting, answers]);

  // Keyboard shortcut: Enter / Space advances when feedback is showing.
  useEffect(() => {
    if (!feedback) return;
    const onKey = (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        advanceToNext();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedback]);

  // Auto-advance on CORRECT answers — kids shouldn't click "Câu tiếp" for every
  // question. Wrong answers keep the manual button (time to read the answer).
  useEffect(() => {
    if (!feedback || !feedback.correct) return;
    const isGachaQ = currentQuestion?.payload?.ui_kind === 'gacha';
    const t = setTimeout(() => advanceToNext(), isGachaQ ? AUTO_ADVANCE_GACHA_MS : AUTO_ADVANCE_MS);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedback]);

  // ── Render guards ────────────────────────────────────────
  const Shell = ({ children }) => (
    <div style={{ minHeight: '100vh', background: HT.yellowBg, fontFamily: FONT_BODY, color: HT.text }}>
      <TopNavBar />
      <main style={{ padding: '24px 24px 60px', maxWidth: 1100, margin: '0 auto', width: '100%' }}>
        {children}
      </main>
    </div>
  );

  if (loading) {
    return <Shell>
      <div style={{ marginTop: 60, textAlign: 'center', color: HT.textSoft, fontWeight: 700, fontSize: 16 }}>
        🦉 Đang chuẩn bị bài tập...
      </div>
    </Shell>;
  }
  if (error) {
    return <Shell>
      <div style={{
        marginTop: 30, padding: 20, background: HT.pinkBg, border: `1px solid ${HT.pink}`,
        borderRadius: 12, color: HT.coralDark, fontWeight: 600,
      }}>⚠ {error}</div>
      <button
        onClick={() => navigate(backUrl)}
        style={ctaBtn()}
      >← Quay lại chủ điểm</button>
    </Shell>;
  }
  if (submitting) {
    return <Shell>
      <div style={{ marginTop: 60, textAlign: 'center', color: HT.textSoft, fontWeight: 700, fontSize: 16 }}>
        💾 Đang lưu kết quả...
      </div>
    </Shell>;
  }
  if (!currentQuestion) {
    if (extending) {
      return <Shell>
        <div style={{ marginTop: 60, textAlign: 'center', color: HT.textSoft, fontWeight: 700 }}>
          ⏳ Đang tải thêm câu hỏi...
        </div>
      </Shell>;
    }
    if (answers.length > 0) {
      return <Shell>
        <div style={{ marginTop: 60, textAlign: 'center', color: HT.textSoft, fontWeight: 700 }}>
          💾 Đang lưu...
        </div>
      </Shell>;
    }
    return <Shell>
      <div style={{
        marginTop: 30, padding: 40, background: HT.white, border: `1px dashed ${HT.border}`,
        borderRadius: 16, textAlign: 'center',
      }}>
        <div style={{ fontSize: 44 }}>🦉</div>
        <div style={{ fontSize: 15, fontWeight: 700, color: HT.text, marginTop: 8 }}>
          Chủ đề này chưa có bài tập
        </div>
        <button onClick={() => navigate(backUrl)} style={{ ...ctaBtn(), marginTop: 16 }}>
          ← Quay lại chủ điểm
        </button>
      </div>
    </Shell>;
  }

  const elapsedSec = Math.floor((Date.now() - sessionStart.current) / 1000);
  const elapsedMin = Math.max(1, Math.round(elapsedSec / 60));
  // Distinct words practiced this session (level mode counter).
  const distinctWords = new Set(answers.map(a => a.leaf_tag_id)).size;
  const progressPct = isLevel
    ? Math.min(100, (currentQuestion.tier / 5) * 100)
    : Math.min(100, (answeredCount / TARGET_QUESTIONS) * 100);

  const immersive = IMMERSIVE_UI_KINDS.has(currentQuestion.payload?.ui_kind);

  // Daily-goal chip (level mode): baseline before session + distinct words this session.
  const goalChip = isLevel && dailyGoal ? (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      background: (dailyGoal.today + distinctWords) >= dailyGoal.goal ? HT.greenBg : HT.white,
      border: `1px solid ${(dailyGoal.today + distinctWords) >= dailyGoal.goal ? HT.green : HT.border}`,
      padding: '4px 10px', borderRadius: 999, boxShadow: SHADOW_SM,
      fontSize: 12, fontWeight: 700, color: HT.brownDark, whiteSpace: 'nowrap',
      fontFamily: FONT_DISPLAY,
    }}>
      🎯 {Math.min(dailyGoal.today + distinctWords, 99)}/{dailyGoal.goal} hôm nay
      {(dailyGoal.today + distinctWords) >= dailyGoal.goal ? ' ✓' : ''}
    </span>
  ) : null;

  // ── Kid-UX overlays shared by both layouts ──
  const kidOverlays = (
    <>
      {/* Word-completed toast (non-blocking, shows during the auto-advance window) */}
      {doneWord && (
        <div className="hu-pop" style={{
          position: 'fixed', top: 136, left: '50%', transform: 'translateX(-50%)',
          zIndex: 1500, padding: '8px 20px',
          background: `linear-gradient(135deg, ${HT.yellow}, ${HT.coral})`,
          color: HT.white, borderRadius: 999, border: `3px solid ${HT.white}`,
          boxShadow: '0 8px 24px rgba(0,0,0,0.18)', pointerEvents: 'none',
          fontFamily: FONT_DISPLAY, fontSize: 16, fontWeight: 800,
        }}>🎉 Hoàn thành từ "{doneWord}"!</div>
      )}
      {/* Newly-mastered toast (from flush deltas) */}
      {masteredWords && (
        <div className="hu-pop" style={{
          position: 'fixed', top: 186, left: '50%', transform: 'translateX(-50%)',
          zIndex: 1500, padding: '8px 20px',
          background: `linear-gradient(135deg, ${HT.green}, ${HT.greenDark})`,
          color: HT.white, borderRadius: 999, border: `3px solid ${HT.white}`,
          boxShadow: '0 8px 24px rgba(0,0,0,0.18)', pointerEvents: 'none',
          fontFamily: FONT_DISPLAY, fontSize: 15, fontWeight: 800,
        }}>🏆 Đã thuộc hẳn: {masteredWords.join(', ')}!</div>
      )}
      {/* Re-teach recap after a word is skipped (2 wrongs) */}
      {recapWord && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 1600,
          background: 'rgba(45,22,60,0.45)', backdropFilter: 'blur(2px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }} onClick={() => setRecapWord(null)}>
          <div className="hu-pop" style={{
            background: HT.white, borderRadius: 24, border: `3px solid ${HT.coral}`,
            boxShadow: `0 10px 0 ${HT.coralPress}, 0 14px 40px rgba(0,0,0,0.25)`,
            padding: 28, maxWidth: 380, width: 'calc(100% - 48px)', textAlign: 'center',
          }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 13, fontWeight: 800, color: HT.coralDark, letterSpacing: 1.5, fontFamily: FONT_BODY }}>
              📖 CÙNG ÔN LẠI NHÉ
            </div>
            {recapWord.image_url && (
              <img src={recapWord.image_url} alt={recapWord.word}
                style={{ maxHeight: 120, maxWidth: '70%', borderRadius: 14, marginTop: 12 }}
                onError={e => { e.target.style.display = 'none'; }} />
            )}
            <div style={{ marginTop: 10, fontSize: 34, fontWeight: 700, fontFamily: FONT_DISPLAY, color: HT.text }}>
              {recapWord.word}
            </div>
            {recapWord.name_vi && (
              <div style={{ marginTop: 4, fontSize: 18, fontWeight: 700, color: HT.greenDark }}>{recapWord.name_vi}</div>
            )}
            {recapWord.definition_vi && (
              <div style={{ marginTop: 8, fontSize: 13, color: HT.textSoft, fontWeight: 600, lineHeight: 1.5 }}>{recapWord.definition_vi}</div>
            )}
            <button onClick={() => setRecapWord(null)} style={{
              marginTop: 16, padding: '12px 30px',
              background: `linear-gradient(135deg, ${HT.green}, ${HT.greenDark})`,
              color: HT.white, border: 'none', borderRadius: 999,
              fontFamily: FONT_DISPLAY, fontSize: 16, fontWeight: 800, cursor: 'pointer',
              boxShadow: `0 4px 0 ${HT.greenDark}`,
            }}>Đã hiểu →</button>
          </div>
        </div>
      )}
      {/* Milestone break every 10 words (level mode) */}
      {milestone != null && !recapWord && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 1600,
          background: 'rgba(45,22,60,0.45)', backdropFilter: 'blur(2px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div className="hu-pop" style={{
            background: HT.white, borderRadius: 24, border: `3px solid ${HT.yellow}`,
            boxShadow: `0 10px 0 ${HT.yellowShadow}, 0 14px 40px rgba(0,0,0,0.25)`,
            padding: 32, maxWidth: 380, width: 'calc(100% - 48px)', textAlign: 'center',
          }}>
            <Confetti />
            <div style={{ fontSize: 52 }}>🎉</div>
            <div style={{ marginTop: 6, fontSize: 22, fontWeight: 700, fontFamily: FONT_DISPLAY, color: HT.text }}>
              Bé vừa luyện {milestone} từ!
            </div>
            <div style={{ marginTop: 6, fontSize: 14, color: HT.textSoft, fontWeight: 600 }}>
              Giỏi quá! Nghỉ một chút hay học tiếp nào?
            </div>
            <div style={{ marginTop: 18, display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
              <button onClick={() => setMilestone(null)} style={{
                padding: '13px 26px',
                background: `linear-gradient(135deg, ${HT.green}, ${HT.greenDark})`,
                color: HT.white, border: 'none', borderRadius: 999,
                fontFamily: FONT_DISPLAY, fontSize: 16, fontWeight: 800, cursor: 'pointer',
                boxShadow: `0 4px 0 ${HT.greenDark}`,
              }}>Học tiếp 💪</button>
              <button onClick={() => { setMilestone(null); handleEarlyEnd(); }} style={{
                padding: '13px 26px',
                background: HT.yellowSoft, border: `1px solid ${HT.yellow}`,
                color: HT.brownDark, borderRadius: 999,
                fontFamily: FONT_DISPLAY, fontSize: 16, fontWeight: 800, cursor: 'pointer',
              }}>Kết thúc 🏁</button>
            </div>
          </div>
        </div>
      )}
    </>
  );

  // ── Immersive themed games: slim header, no bordered card, game fills screen ──
  if (immersive) {
    return (
      <div style={{ minHeight: '100vh', background: HT.yellowBg, fontFamily: FONT_BODY, color: HT.text }}>
        {/* Header wraps on narrow screens: row 1 = ✕ + title + Kết thúc,
            chips (tier/goal/count) flow to their own line instead of
            crushing the title to zero width and overflowing the edge. */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12, rowGap: 6, flexWrap: 'wrap',
          padding: '8px 14px', background: HT.white,
          borderBottom: `1px solid ${HT.border}`, boxShadow: SHADOW_SM,
        }}>
          <button
            onClick={() => navigate(backUrl)}
            aria-label="Đóng"
            style={{
              width: 34, height: 34, borderRadius: '50%',
              background: HT.yellowBg, border: `1px solid ${HT.border}`,
              color: HT.textSoft, fontSize: 17, fontWeight: 700, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}
          >✕</button>
          <div style={{ flex: '1 1 0', minWidth: 140 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: HT.textMuted, fontFamily: FONT_DISPLAY, letterSpacing: 2, whiteSpace: 'nowrap' }}>ĐANG LUYỆN</div>
            <div style={{
              fontSize: 16, fontWeight: 700, fontFamily: FONT_DISPLAY, color: HT.text, lineHeight: 1.1,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {currentQuestion.leafName}
              {parentName ? <span style={{ fontSize: 12, color: HT.textMuted, marginLeft: 8, fontWeight: 600 }}>· {parentName}</span> : null}
            </div>
          </div>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', minWidth: 0,
          }}>
            {isLevel && (
              <button
                onClick={() => navigate(`/vocab/words?level=${encodeURIComponent(levelName)}`)}
                aria-label="Sổ từ vựng"
                title="Sổ từ vựng"
                style={{
                  width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
                  background: HT.yellowBg, border: `1px solid ${HT.border}`,
                  fontSize: 15, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >📖</button>
            )}
            <TierStars currentTier={currentQuestion.tier} />
            {goalChip}
            <span style={{ fontSize: 13, fontFamily: FONT_DISPLAY, fontWeight: 700, color: HT.brownDark, whiteSpace: 'nowrap' }}>
              {isLevel ? `${distinctWords} từ` : `${answeredCount}/${TARGET_QUESTIONS}`}
            </span>
          </div>
          <button
            onClick={handleEarlyEnd}
            style={{
              padding: '8px 14px', background: HT.yellowSoft,
              border: `1px solid ${HT.yellow}`, borderRadius: 999,
              color: HT.brownDark, fontSize: 12, fontWeight: 700, fontFamily: FONT_BODY,
              cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
              marginLeft: 'auto',
            }}
          >⏹ Kết thúc</button>
        </div>

        {feedback && <FeedbackBanner correct={feedback.correct} />}
        {kidOverlays}

        <div className={feedback && !feedback.correct ? 'hu-shake' : ''}>
          <TopicGameRenderer
            key={`${currentQuestion.leafIdx}-${currentQuestion.tier}-${currentQuestion.game_id}-${questionKey}`}
            payload={currentQuestion.payload}
            onAnswer={onAnswer}
            disabled={!!feedback}
          />
        </div>

        {feedback && !feedback.correct && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '4px 16px 24px' }}>
            <button
              onClick={advanceToNext}
              style={{
                padding: '14px 32px',
                background: feedback.correct
                  ? `linear-gradient(135deg, ${HT.green}, ${HT.greenDark})`
                  : `linear-gradient(135deg, ${HT.coral}, ${HT.coralDark})`,
                color: HT.white, border: 'none', borderRadius: 16,
                fontFamily: FONT_DISPLAY, fontSize: 18, fontWeight: 700, cursor: 'pointer',
                boxShadow: `0 4px 0 ${feedback.correct ? HT.greenDark : HT.coralPress}`,
                minWidth: 200,
              }}
            >
              {(!isLevel && answers.length >= TARGET_QUESTIONS) ? '🏁 Xem kết quả' : 'Câu tiếp'} →
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <Shell>
      {/* Top compact bar — close + topic + early end */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12,
        background: HT.white, borderRadius: 999,
        padding: '8px 8px 8px 16px',
        border: `1px solid ${HT.border}`, boxShadow: SHADOW_SM,
      }}>
        <button
          onClick={() => navigate(backUrl)}
          aria-label="Đóng"
          style={{
            width: 36, height: 36, borderRadius: '50%',
            background: HT.yellowBg, border: `1px solid ${HT.border}`,
            color: HT.textSoft, fontSize: 18, fontWeight: 700,
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >✕</button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 11, fontWeight: 700, color: HT.textMuted,
            fontFamily: FONT_DISPLAY, letterSpacing: 2,
          }}>ĐANG LUYỆN</div>
          <div style={{
            fontSize: 18, fontWeight: 700, fontFamily: FONT_DISPLAY,
            color: HT.text, lineHeight: 1.1,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {currentQuestion.leafName}
            {parentName ? <span style={{ fontSize: 12, color: HT.textMuted, marginLeft: 8, fontWeight: 600 }}>· {parentName}</span> : null}
          </div>
        </div>
        <button
          onClick={handleEarlyEnd}
          style={{
            padding: '8px 16px', background: HT.yellowSoft,
            border: `1px solid ${HT.yellow}`, borderRadius: 999,
            color: HT.brownDark, fontSize: 12, fontWeight: 700, fontFamily: FONT_BODY,
            cursor: 'pointer', whiteSpace: 'nowrap',
          }}
        >⏹ Kết thúc</button>
      </div>

      {/* Progress + tier + meta row */}
      <div style={{
        marginTop: 14, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
      }}>
        {/* Big progress bar */}
        <div style={{
          flex: '1 1 320px',
          background: HT.white, borderRadius: 999,
          border: `1px solid ${HT.border}`, boxShadow: SHADOW_SM,
          padding: '8px 16px',
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <span style={{ fontSize: 18 }}>📚</span>
          <div style={{
            flex: 1, height: 12, borderRadius: 999,
            background: HT.yellowBg, overflow: 'hidden',
            border: `1px solid ${HT.border}`,
          }}>
            <div style={{
              width: `${progressPct}%`, height: '100%',
              background: progressPct >= 100
                ? `linear-gradient(90deg, ${HT.green}, ${HT.greenDark})`
                : `linear-gradient(90deg, ${HT.coral}, ${HT.yellow})`,
              transition: 'width 0.4s',
            }} />
          </div>
          <span style={{
            fontSize: 14, fontFamily: FONT_DISPLAY, fontWeight: 700,
            color: HT.brownDark, whiteSpace: 'nowrap',
          }}>
            {isLevel ? `${distinctWords} từ` : `${answeredCount}/${TARGET_QUESTIONS}`}
          </span>
        </div>

        {/* Tier stars */}
        <TierStars currentTier={currentQuestion.tier} />

        {/* Daily goal (level mode) */}
        {goalChip}

        {/* Meta */}
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 10,
          background: HT.white, border: `1px solid ${HT.border}`,
          padding: '6px 12px', borderRadius: 999,
          boxShadow: SHADOW_SM,
          fontSize: 12, fontWeight: 700, color: HT.textSoft,
        }}>
          <span>⏱ {elapsedMin}ph</span>
          <span style={{ color: HT.border }}>|</span>
          <span>🔄 vòng {roundNumber}</span>
          {currentQuestion.wrongCount > 0 && (
            <>
              <span style={{ color: HT.border }}>|</span>
              <span style={{ color: HT.coralDark }}>↻ luyện lại</span>
            </>
          )}
        </div>
      </div>

      {/* Feedback banner — fixed-position toast, no layout shift */}
      {feedback && <FeedbackBanner correct={feedback.correct} />}
      {kidOverlays}

      {/* ── BIG GAME AREA ──────────────────────────────────── */}
      <div style={{
        position: 'relative',
        marginTop: 18,
        background: HT.white,
        border: `2px solid ${HT.border}`,
        borderRadius: 24,
        padding: 'clamp(20px, 3vw, 32px)',
        boxShadow: SHADOW_MD,
        minHeight: 480,
      }}>
        <div className={feedback && !feedback.correct ? 'hu-shake' : ''}>
          <TopicGameRenderer
            key={`${currentQuestion.leafIdx}-${currentQuestion.tier}-${currentQuestion.game_id}-${questionKey}`}
            payload={currentQuestion.payload}
            onAnswer={onAnswer}
            disabled={!!feedback}
          />
        </div>
      </div>

      {/* "Câu tiếp" button — only on WRONG answers (correct auto-advances); child reads the right answer then clicks */}
      {feedback && !feedback.correct && (
        <div style={{
          marginTop: 16, display: 'flex', justifyContent: 'center',
        }}>
          <button
            onClick={advanceToNext}
            style={{
              padding: '16px 36px',
              background: feedback.correct
                ? `linear-gradient(135deg, ${HT.green}, ${HT.greenDark})`
                : `linear-gradient(135deg, ${HT.coral}, ${HT.coralDark})`,
              color: HT.white,
              border: 'none', borderRadius: 16,
              fontFamily: FONT_DISPLAY, fontSize: 18, fontWeight: 700,
              cursor: 'pointer',
              boxShadow: `0 4px 0 ${feedback.correct ? HT.greenDark : HT.coralPress}`,
              display: 'inline-flex', alignItems: 'center', gap: 10,
              transition: 'transform 0.1s, box-shadow 0.1s',
              minWidth: 220,
              justifyContent: 'center',
            }}
            onMouseDown={e => {
              e.currentTarget.style.transform = 'translateY(2px)';
              e.currentTarget.style.boxShadow = `0 2px 0 ${feedback.correct ? HT.greenDark : HT.coralPress}`;
            }}
            onMouseUp={e => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = `0 4px 0 ${feedback.correct ? HT.greenDark : HT.coralPress}`;
            }}
            onMouseLeave={e => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = `0 4px 0 ${feedback.correct ? HT.greenDark : HT.coralPress}`;
            }}
          >
            {(!isLevel && answers.length >= TARGET_QUESTIONS) ? '🏁 Xem kết quả' : 'Câu tiếp'} →
          </button>
        </div>
      )}

      {/* Hint */}
      {feedback && !feedback.correct && (
        <div style={{
          marginTop: 8, textAlign: 'center',
          fontSize: 12, fontWeight: 600, color: HT.textMuted,
        }}>
          ⌨ Bạn cũng có thể nhấn <kbd style={{
            padding: '1px 6px', background: HT.white, border: `1px solid ${HT.border}`,
            borderRadius: 4, fontFamily: 'monospace', fontSize: 11,
          }}>Enter</kbd> hoặc <kbd style={{
            padding: '1px 6px', background: HT.white, border: `1px solid ${HT.border}`,
            borderRadius: 4, fontFamily: 'monospace', fontSize: 11,
          }}>Space</kbd> để qua câu tiếp
        </div>
      )}
    </Shell>
  );
}

const ctaBtn = () => ({
  marginTop: 16,
  padding: '12px 22px',
  background: HT.coral, color: HT.white,
  border: 'none', borderRadius: 14,
  fontFamily: FONT_DISPLAY, fontSize: 14, fontWeight: 700,
  cursor: 'pointer',
  boxShadow: `0 3px 0 ${HT.coralPress}`,
});
